import { ref, readonly, watch, computed, nextTick } from 'vue';
import { presets } from '@/helpers/theme';
import { debounce, copy } from '@/helpers/utilities';

let $http = null;
let checkSession = () => {};
let courseSaveTimer = null;
let screenSaveTimer = null;
let questionSaveTimer = null;
let screenOrderSaveTimer = null;
let saveCallback = null;

const DEFAULT_COURSE = {
    title: 'Course title',
    finish: {},
    screens: [],
    theme: presets[0],
    default: true
};

const state = ref({
    course: DEFAULT_COURSE,
    isSidebarOpen: true,
    items: {}
});

const generateTempId = () => {
    return Math.random().toString(36).substr(2, 9);
};

// --- Course ---

export const getCourse = async (id) => {
    return $http.get(`/course/${id}`)
        .then(({ data }) => {
            setCourse({ ...data, default: false });
            return data;
        })
        .catch((e) => checkSession && checkSession(e));
};

export const setCourse = (course) => {
    if (course.id && state.value.course.id && course.id !== state.value.course.id) state.value.course = DEFAULT_COURSE;
    if (!course) return course;
    const unchanged = copy({ ...state.value.course, screens: undefined, theme: undefined });

    if (!state.value.course.finish && !course.finish) course.finish = {};
    if (!state.value.course.screens && !course.screens) course.screens = state.value.course.screens || [];
    if (!state.value.course.theme && (!course.theme || Array.isArray(course.theme))) course.theme = presets[0];

    // HACK: If screens return as an object, convert to array and check sequencing
    if (!Array.isArray(course.screens) && !Array.isArray(state.value.course.screens)) {
        course.screens = Object.values(course.screens);
        course.screens.sort((a, b) => a.sequence - b.sequence);
        course.screens.forEach((screen, index) => screen.sequence = index + 1);
    }

    state.value.course = { ...state.value.course, ...course, theme: { ...state.value.course.theme, ...course.theme } };

    if (unchanged.default) return;
    if (JSON.stringify(unchanged) !== JSON.stringify({ ...state.value.course, screens: undefined })) {
        saveCourseTrigger(state.value.course);
    }
};

export const createCourse = (course) => {
    const courseData = { ...state.value.course, ...course };
    if (courseData.screens) {
        courseData.screens = courseData.screens.map(screen => ({
            ...screen,
            type: screen.type.id,
            questions: screen.questions?.map(question => ({
                ...question,
                klass: question.questionType.template === 'dropDown' ? 'dropdown' : 'simple'
            }))
        }));
    }

    return $http.post('/course', courseData)
        .then(({ data }) => {
            setCourse({ ...data, default: false });
            return data;
        })
        .catch((e) => checkSession && checkSession(e));
};


const saveCourseTrigger = (course) => {
    if (!course.id) return saveCourseLocal();
    return courseSaveTimer = debounce(() => {
        return saveCourse(course);
    }, 1000, courseSaveTimer);
};

const saveCourse = (course) => {
    if (!course.id) return Promise.resolve();

    return $http.put(`/course/${course.id}`, course)
        .then(() => saveCallback && saveCallback())
        .catch((e) => checkSession && checkSession(e));
};

const saveCourseLocal = () => {
    if (state.value.course.id) return;
    localStorage.setItem('coassemble:localcourse', JSON.stringify(state.value.course));
};

// --- Screens ---

export const getScreenIndex = (screen, index = -1) => {
    let _index = -1;
    if (index >= 0) {
        _index = index;
    } else if (screen.id) {
        _index = state.value.course.screens.findIndex(({ id }) => id === screen.id);
    }
    if (_index < 0 || _index === undefined) {
        if (screen.sequence) _index = state.value.course.screens.findIndex(({ sequence }) => sequence === screen.sequence);
    }
    return _index;
};

export const addScreen = (screen, index = state.value.course.screens.length) => {
    if (!screen.type) return;
    state.value.course.screens.splice(index, 0, { ...screen, tempId: generateTempId() });
};

export const createScreen = (screen, index, add = true) => {
    if (add) addScreen(screen, index);
    if (!state.value.course.id) return Promise.resolve(screen);
    return $http.post(`/course/${state.value.course.id}/screen`, { ...screen, type: screen.type.id })
        .then(({ data }) => {
            updateScreen({ id: data.id }, index);
        })
        .catch((e) => checkSession && checkSession(e));
};

export const updateScreen = (screen, index = -1) => {
    if (screen.type && screen.type.name === 'finish') {
        if (screen.object && screen.object.start && screen.sequence === 0) return updateStartScreen(screen);
        if (screen.object && screen.object.finish) return updateFinishScreen(screen);
    }

    const _index = getScreenIndex(screen, index);
    if (_index < 0) return;

    const unchanged = copy(state.value.course.screens[_index]);
    const updated = { ...state.value.course.screens[_index], ...screen };
    if (JSON.stringify(unchanged) === JSON.stringify(updated)) return false;

    state.value.course.screens.splice(_index, 1, updated);
    saveScreenTrigger(updated);
    return true;
};

const createQuestion = (screen, question, questionIndex = -1) => {
    const _index = getScreenIndex(screen);
    if (_index < 0) return;

    const _screen = state.value.course.screens[_index];

    if (questionIndex < 0) {
        _screen.questions.push(question);
    } else {
        _screen.questions.splice(questionIndex, 1, question);
    }

    if (!state.value.course.id || !_screen.id) return Promise.resolve(question);

    return $http.post(`/course/${state.value.course.id}/screen/${_screen.id}/question`, {
        'text': question.text,
        'answers': question.answers,
        'segments': question.segments,
        'questionType': question.questionType
    }).then(({ data }) => {
        updateQuestion(screen, { ...data, 'questionType': question.questionType }, questionIndex);
        return data;
    });
};

const addQuestion = (screen, question, questionIndex = -1) => {
    if (questionIndex < 0) {
        screen.questions.push(question);
    } else {
        screen.questions.splice(questionIndex, 1, question);
    }
};

const updateQuestion = (screen, question, questionIndex = -1) => {
    const _index = getScreenIndex(screen);
    if (_index < 0) return;

    const unchanged = copy(state.value.course.screens[_index].questions[questionIndex]);
    const updated = copy({ ...state.value.course.screens[_index].questions[questionIndex], ...question });
    if (JSON.stringify(unchanged) === JSON.stringify(updated)) return false;

    state.value.course.screens[_index].questions.splice(questionIndex, 1, updated);
    saveQuestionTrigger(screen, updated);
    return updated;
};

const deleteQuestion = (screen, question, questionIndex = -1) => {
    const _index = getScreenIndex(screen);
    if (_index < 0) return;

    state.value.course.screens[_index].questions.splice(questionIndex, 1);
    return $http.delete(`/course/${state.value.course.id}/screen/${screen.id}/question/${question.id}`)
        .then(() => saveCallback && saveCallback())
        .catch((e) => checkSession && checkSession(e));
};

const saveQuestionTrigger = (screen, question) => {
    return questionSaveTimer = debounce(() => {
        return saveQuestion(screen, question);
    }, 1000, questionSaveTimer);
};

const saveQuestion = (screen, question) => {
    if (!question.id) return;
    return $http.put(`/course/${state.value.course.id}/screen/${screen.id}/question/${question.id}`, question)
        .then(() => saveCallback && saveCallback())
        .catch((e) => checkSession && checkSession(e));
};

const updateStartScreen = (screen) => {
    const unchanged = copy({
        title: state.value.course.title,
        description: state.value.course.description,
        image: state.value.course.image,
        start: state.value.course.start
    });
    const updated = {
        title: screen.title,
        description: screen.description,
        image: screen.asset,
        start: screen.object
    };
    if (JSON.stringify(unchanged) === JSON.stringify(updated)) return false;
    setCourse(updated);
    return true;
};

const updateFinishScreen = (screen) => {
    const unchanged = copy(state.value.course.finish);
    const updated = {
        title: screen.title,
        description: screen.description,
        asset: screen.asset,
        ...screen.object
    };
    if (JSON.stringify(unchanged) === JSON.stringify(updated)) return false;
    setCourse({ finish: updated });
    return true;
};

const saveScreenTrigger = (screen) => {
    if (!state.value.course.screens.every(s => s.id)) return;
    return screenSaveTimer = debounce(() => {
        return saveScreen(screen);
    }, 1000, screenSaveTimer);
};

const saveScreen = (screen) => {
    if (!screen.id) return;
    const type = screen.type.id;
    return $http.put(`/course/${state.value.course.id}/screen/${screen.id}`, { ...screen, type })
        .then(() => saveCallback && saveCallback())
        .catch((e) => checkSession && checkSession(e));
};

export const deleteScreen = (index) => {
    const deleted = state.value.course.screens.splice(index, 1)[0];
    if (!deleted.id) return;
    return $http.delete(`/course/${state.value.course.id}/screen/${deleted.id}`)
        .then(() => saveCallback && saveCallback())
        .catch((e) => checkSession && checkSession(e));
};

let isMoving = false;
export const moveScreen = (from, to) => {
    if (to < 0 || to >= state.value.course.screens.length) return;
    isMoving = true;
    const currentSequence = state.value.course.screens[from].sequence;
    const targetSequence = state.value.course.screens[to].sequence;
    changeArrayPosition(state.value.course.screens, from, to);
    state.value.course.screens[to].sequence = targetSequence;
    state.value.course.screens[from].sequence = currentSequence;
    
    nextTick(() => {
        isMoving = false;
        saveScreenOrderTrigger();
    });
};

const changeArrayPosition = (arr, from, to) => {
    [ arr[from], arr[to] ] = [ arr[to], arr[from] ];
    return arr;
};

const resequenceScreens = () => {
    if (isMoving) return;
    if (!state.value.course.screens) return;

    const screens = copy(state.value.course.screens);
    screens.sort((a, b) => a.sequence - b.sequence);
    screens.forEach((screen, index) => screen.sequence = index + 1);
    const sequenceAfter = screens.map(({ sequence }) => sequence);
    const sequenceBefore = sequences.value;

    if (sequenceBefore.join(',') === sequenceAfter.join(',')) return;
    state.value.course.screens.sort((a, b) => a.sequence - b.sequence);
    state.value.course.screens.forEach((screen, index) => screen.sequence = index + 1);
    saveScreenOrderTrigger();
};

const saveScreenOrderTrigger = () => {
    return screenOrderSaveTimer = debounce(() => {
        return saveScreenOrder();
    }, 1000, screenOrderSaveTimer);
};

const saveScreenOrder = () => {
    if (!state.value.course.id) return;
    const screens = state.value.course.screens.map(({ id, sequence }) => ({ [id]: sequence })).reduce((a, b) => ({ ...a, ...b }), {});
    return $http
        .put(`/course/${state.value.course.id}/reorder`, { screens })
        .catch((e) => checkSession && checkSession(e));
};

const sequences = computed(() => state.value.course.screens.map(({ sequence }) => sequence));
const hasDuplicateSequences = computed(() => new Set(sequences.value).size !== sequences.value.length);
const hasDisorderedSequence = computed(() => sequences.value.some((sequence, index) => sequence !== index + 1));
watch(hasDuplicateSequences, (to) => to && resequenceScreens());
watch(hasDisorderedSequence, (to) => to && resequenceScreens());

// --- Misc ---

const toggleSidebar = () => {
    state.value.isSidebarOpen = !state.value.isSidebarOpen;
};
const openSidebar = () => {
    state.value.isSidebarOpen = true;
};
const closeSidebar = () => {
    state.value.isSidebarOpen = false;
};

const addGeneratedItems = (key, items) => {
    state.value.items[key] = items;
};
const clearGeneratedItems = (key) => {
    state.value.items[key] = null;
};

const setHttp = (http, check = null) => {
    $http = http;
    if (check) checkSession = check;
};

const setSaveCallback = (callback) => {
    saveCallback = callback;
};

export default readonly({
    getCourse,
    setCourse,
    createCourse,
    saveCourse,

    getScreenIndex,
    addScreen,
    createScreen,
    updateScreen,
    addQuestion,
    createQuestion,
    updateQuestion,
    deleteQuestion,

    moveScreen,
    deleteScreen,

    toggleSidebar,
    openSidebar,
    closeSidebar,
    addGeneratedItems,
    clearGeneratedItems,

    setHttp,
    setSaveCallback,

    state
});
